import React, { useState, useEffect } from 'react';
import { Plus, Printer, Download, Upload, Receipt, FileWarning, Send, Ban, RotateCcw, Trash2, AlertTriangle, Lock } from 'lucide-react';
import api from '../utils/api';
import { fmtDate, fmtCurrency, statusColor } from '../utils/helpers';
import { PageHeader, DataTable, SearchBar, StatusBadge, Modal, FormField } from '../components/Table';
import { patchRows } from '../components/gridShared';
import { PaymentExportModal, PaymentImportModal } from '../components/PaymentSheet';
import ReceiptsTab from '../components/ReceiptsTab';
import BulkActionBar from '../components/BulkActionBar';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import { downloadExport } from '../utils/exportUtils';
import useGstRate from '../utils/useGstRate';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Inline cell for the invoice DataTable. The grid modules use the shared
// EditableCell inside a <td>; here the cell content sits inside DataTable's own
// <td>, so this wrapper reuses the same interaction contract (click/Enter to
// edit, Enter/blur commits, Esc cancels, green flash on save) without a <td>.
function EditableCellInline({ row, col, display, editOptions, onSave }) {
  const [editing, setEditing] = useState(false);
  const [flash, setFlash] = useState(null);
  const [val, setVal] = useState('');

  const start = () => { setVal(row[col] ?? ''); setEditing(true); };
  const commit = async v => {
    setEditing(false);
    if (String(v ?? '') === String(row[col] ?? '')) return;
    try { await onSave(v); setFlash('ok'); setTimeout(() => setFlash(null), 700); }
    catch { setFlash('err'); setTimeout(() => setFlash(null), 900); }
  };

  if (editing) {
    if (editOptions?.type === 'select') {
      return (
        <select autoFocus defaultValue={row[col] ?? ''} className="input-field text-xs py-0.5"
          onBlur={e => commit(e.target.value)}
          onChange={e => commit(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}>
          {editOptions.options.map(o => <option key={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input
        autoFocus type={editOptions?.type === 'date' ? 'date' : editOptions?.type === 'number' ? 'number' : 'text'}
        value={val} onChange={e => setVal(e.target.value)}
        className="input-field text-xs py-0.5 w-full min-w-[90px]"
        onBlur={e => commit(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(e.currentTarget.value); }
          if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
        }}
      />
    );
  }
  return (
    <span
      tabIndex={0}
      onClick={start}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); start(); } }}
      className={`cursor-text rounded px-1 -mx-1 outline-none focus:ring-2 focus:ring-[#1e3a5f]/30 hover:bg-blue-50/60 transition-colors
        ${flash === 'ok' ? 'bg-emerald-100' : flash === 'err' ? 'bg-red-100' : ''}`}
      title="Click or press Enter to edit"
    >
      {display}
    </span>
  );
}

// Document lifecycle (payment status stays derived from receipts)
const INV_STATES = [
  { v: 'draft', label: 'Draft', cls: 'bg-slate-100 text-slate-600' },
  { v: 'issued', label: 'Issued', cls: 'bg-blue-50 text-blue-700' },
  { v: 'cancelled', label: 'Cancelled', cls: 'bg-red-50 text-red-600' },
];
const INV_STATUSES = ['Draft', 'Sent', 'Partial', 'Paid', 'Overdue'];
const PAYMENT_MODES = ['Cash', 'Cheque', 'NEFT', 'RTGS', 'UPI'];

// ── Referring CAs tab: sub-contracted work billed to another CA ───────────────
function ReferringCAsTab({ can }) {
  const [cas, setCas] = useState([]);
  const [editCa, setEditCa] = useState(null);     // null | {} (new) | record
  const [detail, setDetail] = useState(null);     // { ...ca, invoices, clients, ledger }
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/referring-cas').then(r => setCas(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openDetail = async ca => {
    const [d, l] = await Promise.all([
      api.get(`/referring-cas/${ca.id}`),
      api.get(`/referring-cas/${ca.id}/ledger`),
    ]);
    setDetail({ ...d.data, ledger: l.data });
  };

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editCa.id) await api.put(`/referring-cas/${editCa.id}`, editCa);
      else await api.post('/referring-cas', editCa);
      toast.success('Saved'); setEditCa(null); load();
    } catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const F = (label, key, props = {}) => (
    <FormField label={label}>
      <input value={editCa[key] || ''} onChange={e => setEditCa(c => ({ ...c, [key]: e.target.value }))} className="input-field" {...props} />
    </FormField>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">CAs who refer their clients' work — invoices go to the CA, not the end-client.</p>
        {can('billing', 'create') && (
          <button onClick={() => setEditCa({})} className="btn-primary text-sm flex items-center gap-2"><Plus size={14} /> Add Referring CA</button>
        )}
      </div>
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-xs text-gray-500 uppercase">
            {['Name / Firm', 'Contact', 'Clients', 'Invoices', 'Total Billed', 'Outstanding', ''].map(h => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {cas.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-gray-400">No referring CAs yet.</td></tr>}
            {cas.map(ca => (
              <tr key={ca.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(ca)}>
                <td className="px-3 py-2 font-medium text-[#1e3a5f]">{ca.name}</td>
                <td className="px-3 py-2 text-gray-500 text-xs">{ca.contact_person || '—'}{ca.mobile ? ` · ${ca.mobile}` : ''}</td>
                <td className="px-3 py-2">{ca.client_count}</td>
                <td className="px-3 py-2">{ca.invoice_count}</td>
                <td className="px-3 py-2 font-semibold">{fmtCurrency(ca.total_billed)}</td>
                <td className="px-3 py-2">{ca.total_outstanding > 0 ? <span className="text-red-600 font-semibold">{fmtCurrency(ca.total_outstanding)}</span> : <span className="text-green-600">Nil</span>}</td>
                <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                  {can('billing', 'edit') && <button onClick={() => setEditCa(ca)} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editCa && (
        <Modal title={editCa.id ? `Edit — ${editCa.name}` : 'Add Referring CA'} onClose={() => setEditCa(null)}>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><FormField label="Name / Firm" required><input required value={editCa.name || ''} onChange={e => setEditCa(c => ({ ...c, name: e.target.value }))} className="input-field" autoFocus /></FormField></div>
              {F('Contact Person', 'contact_person')}
              {F('Mobile', 'mobile')}
              {F('Email', 'email', { type: 'email' })}
              {F('GSTIN (optional)', 'gstin')}
            </div>
            <FormField label="Address"><textarea value={editCa.address || ''} onChange={e => setEditCa(c => ({ ...c, address: e.target.value }))} className="input-field" rows={2} /></FormField>
            <FormField label="Payment Terms (notes)"><textarea value={editCa.payment_terms || ''} onChange={e => setEditCa(c => ({ ...c, payment_terms: e.target.value }))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Saving…' : 'Save'}</button><button type="button" onClick={() => setEditCa(null)} className="btn-secondary flex-1">Cancel</button></div>
          </form>
        </Modal>
      )}

      {detail && (
        <Modal title={`${detail.name} — Referring CA`} onClose={() => setDetail(null)} size="lg">
          <div className="grid grid-cols-3 gap-3 mb-4 text-center">
            <div className="bg-gray-50 rounded-xl p-3"><div className="text-lg font-bold">{fmtCurrency(detail.total_billed)}</div><div className="text-xs text-gray-500">Total Billed</div></div>
            <div className="bg-gray-50 rounded-xl p-3"><div className={`text-lg font-bold ${detail.total_outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtCurrency(detail.total_outstanding)}</div><div className="text-xs text-gray-500">Outstanding</div></div>
            <div className="bg-gray-50 rounded-xl p-3"><div className="text-lg font-bold">{detail.clients.length}</div><div className="text-xs text-gray-500">End-Clients</div></div>
          </div>
          {detail.clients.length > 0 && (
            <p className="text-xs text-gray-500 mb-4">Clients billed via this CA: {detail.clients.map(c => c.name).join(', ')}</p>
          )}
          <h4 className="font-semibold text-sm text-gray-700 mb-2">Ledger</h4>
          {detail.ledger.entries.length === 0 ? <p className="text-xs text-gray-400">No transactions yet.</p> : (
            <table className="w-full text-xs">
              <thead><tr className="text-gray-400 uppercase border-b">
                <th className="text-left py-1.5">Date</th><th className="text-left py-1.5">Particulars</th>
                <th className="text-right py-1.5">Dr</th><th className="text-right py-1.5">Cr</th><th className="text-right py-1.5">Balance</th>
              </tr></thead>
              <tbody>
                {detail.ledger.entries.map((e, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5">{fmtDate(e.date)}</td>
                    <td className="py-1.5">{e.particulars}</td>
                    <td className="py-1.5 text-right">{e.debit ? fmtCurrency(e.debit) : '—'}</td>
                    <td className="py-1.5 text-right text-green-700">{e.credit ? fmtCurrency(e.credit) : '—'}</td>
                    <td className="py-1.5 text-right font-semibold">{fmtCurrency(Math.abs(e.balance))} {e.balance_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex justify-end mt-4"><button onClick={() => setDetail(null)} className="btn-secondary px-6">Close</button></div>
        </Modal>
      )}
    </div>
  );
}

// ── Record Payment modal (supports multiple partial payments per invoice) ─────
function RecordPaymentModal({ invoice, onClose, onSaved }) {
  const [f, setF] = useState({
    amount: invoice.outstanding, // pre-filled with full outstanding, editable
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'NEFT', bank_or_upi: '', reference_number: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/billing/${invoice.id}/payments`, f);
      toast.success(`Payment recorded (${r.data.receipt_number}) — ${r.data.status === 'Paid' ? 'invoice fully paid' : `₹${Number(r.data.outstanding).toLocaleString('en-IN')} outstanding`}`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.error || 'Failed to record payment'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Record Payment — ${invoice.invoice_number}`} onClose={onClose}>
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 flex items-center justify-between text-sm">
        <span className="text-gray-600">{invoice.client_name}</span>
        <span>Outstanding: <strong className="text-red-600">{fmtCurrency(invoice.outstanding)}</strong> <span className="text-gray-400">of {fmtCurrency(invoice.total)}</span></span>
      </div>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Amount received (₹)" required>
            <input required type="number" step="0.01" min="0.01" value={f.amount}
              onChange={e => setF(p => ({ ...p, amount: e.target.value }))} className="input-field" autoFocus />
          </FormField>
          <FormField label="Payment date">
            <input type="date" value={f.payment_date} onChange={e => setF(p => ({ ...p, payment_date: e.target.value }))} className="input-field" />
          </FormField>
          <FormField label="Payment mode">
            <select value={f.payment_mode} onChange={e => setF(p => ({ ...p, payment_mode: e.target.value }))} className="input-field">
              {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
            </select>
          </FormField>
          <FormField label="Bank / UPI">
            <input value={f.bank_or_upi} onChange={e => setF(p => ({ ...p, bank_or_upi: e.target.value }))} className="input-field" placeholder="e.g. HDFC / name@upi" />
          </FormField>
          <FormField label="Reference number">
            <input value={f.reference_number} onChange={e => setF(p => ({ ...p, reference_number: e.target.value }))} className="input-field" placeholder="UTR / cheque no." />
          </FormField>
        </div>
        <FormField label="Notes">
          <textarea value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} className="input-field" rows={2} />
        </FormField>
        {parseFloat(f.amount) > invoice.outstanding + 0.005 && (
          <p className="text-xs text-orange-600">Amount exceeds the outstanding balance — this will over-credit the ledger.</p>
        )}
        <div className="flex gap-3 pt-1">
          <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Saving…' : 'Record Payment'}</button>
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

export default function BillingPage() {
  const gstRate = useGstRate();
  const { can } = useAuth();
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showView, setShowView] = useState(null);
  const [form, setForm] = useState({ services: [{ name: '', amount: '', _k: Math.random() }], invoice_date: new Date().toISOString().split('T')[0], status: 'Draft' });
  // Ledger
  const [ledgerClientId, setLedgerClientId] = useState('');
  const [ledgerClientName, setLedgerClientName] = useState('');
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoaded, setLedgerLoaded] = useState(false);
  // Unbilled work (Feature 5: primary actionable location for invoice generation)
  const [unbilled, setUnbilled] = useState([]);
  const [unbilledSelected, setUnbilledSelected] = useState(new Set());
  const [unbilledFees, setUnbilledFees] = useState({});
  const [includeGstU, setIncludeGstU] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  // Record Payment + per-invoice payment history
  const [payInvoice, setPayInvoice] = useState(null);
  const [viewPayments, setViewPayments] = useState(null); // history for showView invoice
  const [receiptForm, setReceiptForm] = useState({ amount: '', payment_mode: 'Bank Transfer', receipt_date: new Date().toISOString().split('T')[0] });
  // Lifecycle dialogs
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [stateFilter, setStateFilter] = useState('');
  // Payment update sheet (export → fill in Excel → import)
  const [payExport, setPayExport] = useState(false);
  const [payImport, setPayImport] = useState(false);
  // Bulk actions on the invoice grid
  const [selected, setSelected] = useState(new Set());
  const [bulkDue, setBulkDue] = useState('');
  const [bulkNote, setBulkNote] = useState(false);
  const [bulkNoteText, setBulkNoteText] = useState('');
  const [bulkCancel, setBulkCancel] = useState(false);
  const [bulkCancelReason, setBulkCancelReason] = useState('');
  const [bulkPay, setBulkPay] = useState(false);
  const [bulkPayForm, setBulkPayForm] = useState({ payment_date: new Date().toISOString().split('T')[0], payment_mode: 'NEFT', reference_number: '' });

  const load = () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    api.get('/billing', { params }).then(r => setInvoices(r.data));
  };

  useEffect(() => {
    api.get('/clients', { params: { status: 'Active' } }).then(r => setClients(r.data));
    api.get('/billing/reports/outstanding').then(r => setOutstanding(r.data));
  }, []);
  useEffect(() => { load(); }, [statusFilter]);

  useEffect(() => {
    if (tab === 'unbilled') {
      api.get('/ledger/unbilled').then(r => setUnbilled(r.data)).catch(() => {});
    }
  }, [tab]);

  const loadLedger = (clientId) => {
    if (!clientId) return;
    api.get('/ledger/ledger', { params: { client_id: clientId } })
      .then(r => { setLedgerData(r.data); setLedgerLoaded(true); })
      .catch(() => {});
  };

  const saveReceipt = async e => {
    e.preventDefault();
    if (!ledgerClientId) return toast.error('Select a client first');
    try {
      await api.post('/ledger/receipts', { ...receiptForm, client_id: ledgerClientId });
      toast.success('Receipt saved');
      setShowReceiptModal(false);
      loadLedger(ledgerClientId);
    } catch { toast.error('Failed'); }
  };

  const reloadUnbilled = () => api.get('/ledger/unbilled').then(r => setUnbilled(r.data)).catch(() => {});

  // Open invoice view + fetch its individual payment history
  const openInvoiceView = row => {
    setShowView(row);
    setViewPayments(null);
    api.get(`/billing/${row.id}/payments`).then(r => setViewPayments(r.data)).catch(() => setViewPayments({ payments: [] }));
  };

  // Generate invoice for one client's selected work items (FY-scoped numbering,
  // marks items billed via the shared work-ledger flow)
  // Direct groups bill the end-client; Referring-CA groups produce ONE invoice
  // addressed to the CA, itemized per end-client (server labels the lines)
  const generateForGroup = async (group, items) => {
    setGenerating(true);
    try {
      const svc = items.map(i => ({
        ...i,
        fee_amount: parseFloat(unbilledFees[i.work_type + ':' + i.id] ?? i.default_fee ?? 0) || 0,
      }));
      const r = await api.post('/work-ledger/generate-invoice', {
        ...(group.isRca ? { referring_ca_id: group.rcaId } : { client_id: parseInt(group.clientId) }),
        items: svc,
        include_gst: includeGstU,
        invoice_date: new Date().toISOString().split('T')[0],
      });
      toast.success(`Invoice ${r.data.invoice_number} created${group.isRca ? ` — billed to ${group.name}` : ''} — record payment from the Invoices tab`);
      setUnbilledSelected(new Set());
      reloadUnbilled();
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed to generate invoice'); }
    finally { setGenerating(false); }
  };

  const calcTotals = services => {
    const subtotal = services.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
    const gst = subtotal * (gstRate / 100);
    return { subtotal, gst_amount: gst, total: subtotal + gst };
  };

  const handleSave = async e => {
    e.preventDefault();
    try {
      const svcs = form.services.filter(s => s.name && s.amount);
      const totals = calcTotals(svcs);
      await api.post('/billing', { ...form, services: svcs, ...totals });
      toast.success('Invoice created'); setShowModal(false);
      setForm({ services: [{ name: '', amount: '', _k: Math.random() }], invoice_date: new Date().toISOString().split('T')[0], status: 'Draft' });
      load();
    } catch { toast.error('Failed'); }
  };

  // ── Lifecycle actions: issue / cancel / revise / delete-draft ───────────────
  const issueInvoice = async row => {
    try {
      const r = await api.post(`/billing/${row.id}/issue`);
      setInvoices(prev => patchRows(prev, row.id, { state: 'issued', invoice_number: r.data.invoice_number }));
      toast.success(`Issued as ${r.data.invoice_number}`);
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not issue'); }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) { toast.error('Please type a reason'); return; }
    try {
      await api.post(`/billing/${cancelTarget.id}/cancel`, { reason: cancelReason.trim() });
      setInvoices(prev => patchRows(prev, cancelTarget.id, { state: 'cancelled', cancel_reason: cancelReason.trim() }));
      toast.success(`${cancelTarget.invoice_number} cancelled — the number stays in the sequence`);
      setCancelTarget(null); setCancelReason('');
      api.get('/billing/reports/outstanding').then(r => setOutstanding(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not cancel'); }
  };

  const reviseInvoice = async row => {
    try {
      const r = await api.post(`/billing/${row.id}/revise`);
      toast.success(`${row.invoice_number} cancelled — editable copy created as ${r.data.new_invoice_number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not revise'); }
  };

  // ── Bulk actions ────────────────────────────────────────────────────────────
  const selectedRows = () => filteredInvoices.filter(i => selected.has(i.id));
  const selectionSummary = () => {
    const rows = selectedRows();
    return {
      drafts: rows.filter(r => r.state === 'draft').length,
      issued: rows.filter(r => r.state === 'issued').length,
      withBalance: rows.filter(r => r.state === 'issued' && r.outstanding > 0.005),
      issuedTotal: rows.filter(r => r.state === 'issued').reduce((s, r) => s + (r.total || 0), 0),
    };
  };

  const runBulk = async (fn, successMsg) => {
    try {
      const r = await fn(Array.from(selected));
      toast.success(typeof successMsg === 'function' ? successMsg(r.data) : successMsg);
      setSelected(new Set());
      load();
      api.get('/billing/reports/outstanding').then(x => setOutstanding(x.data)).catch(() => {});
      return r;
    } catch (e) { toast.error(e?.response?.data?.error || 'Bulk action failed'); }
  };

  const bulkIssue = () => runBulk(
    ids => api.post('/billing/bulk-issue', { ids }),
    d => `${d.issued.length} invoice${d.issued.length !== 1 ? 's' : ''} issued${d.skipped ? ` (${d.skipped} skipped — not drafts)` : ''}`);

  const bulkSetDue = () => runBulk(
    ids => api.post('/billing/bulk-update', { ids, updates: { due_date: bulkDue } }),
    d => `Due date set on ${d.updated} invoice${d.updated !== 1 ? 's' : ''}`).then(() => setBulkDue(''));

  const bulkAddNote = () => runBulk(
    ids => api.post('/billing/bulk-update', { ids, updates: { notes: bulkNoteText.trim(), append: true } }),
    d => `Note added to ${d.updated} invoice${d.updated !== 1 ? 's' : ''}`).then(() => { setBulkNote(false); setBulkNoteText(''); });

  const doBulkCancel = () => runBulk(
    ids => api.post('/billing/bulk-cancel', { ids, reason: bulkCancelReason.trim() }),
    d => `${d.cancelled} invoice${d.cancelled !== 1 ? 's' : ''} cancelled — numbers kept in the sequence`)
    .then(() => { setBulkCancel(false); setBulkCancelReason(''); });

  const doBulkPayment = () => runBulk(
    ids => api.post('/billing/bulk-payment', { ids, ...bulkPayForm }),
    d => `${d.paid} invoice${d.paid !== 1 ? 's' : ''} settled — ${fmtCurrency(d.total)} recorded`)
    .then(() => setBulkPay(false));

  const exportSelected = () => {
    const rows = selectedRows();
    if (!rows.length) return;
    const head = ['Invoice No', 'State', 'Client', 'Date', 'Total', 'Received', 'Balance'];
    const csv = [head.join(','), ...rows.map(r => [r.invoice_number, r.state, r.client_name, r.invoice_date, r.total, r.paid_amount, r.outstanding]
      .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `KUA_Invoices_Selected_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const deleteDraft = async row => {
    try {
      await api.delete(`/billing/${row.id}`);
      setInvoices(prev => prev.filter(i => i.id !== row.id));
      toast.success('Draft deleted');
      setDeleteTarget(null);
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not delete'); }
  };

  const filteredInvoices = invoices
    .filter(i => !stateFilter || (i.state || 'draft') === stateFilter)
    .filter(i => !search || i.client_name?.toLowerCase().includes(search.toLowerCase()) || i.invoice_number?.toLowerCase().includes(search.toLowerCase()));

  // Status is DERIVED server-side from actual payments (Paid/Partial/Unpaid/Overdue)
  const STATUS_BADGE = {
    Paid: 'bg-green-100 text-green-700',
    Partial: 'bg-yellow-100 text-yellow-700',
    Overdue: 'bg-red-100 text-red-700',
    Unpaid: 'bg-gray-100 text-gray-600',
  };
  const STATUS_LABEL = { Paid: 'Paid', Partial: 'Partially paid', Overdue: 'Overdue', Unpaid: 'Unpaid' };

  const cancelled = row => row.state === 'cancelled';
  const strike = row => cancelled(row) ? 'line-through opacity-60' : '';

  // ── Inline editing (Excel-style, same behaviour as the Client grid) ─────────
  // Always editable: due date, notes, contact. Everything else only on drafts.
  const ALWAYS_EDITABLE = ['due_date', 'notes', 'contact_person'];
  const editableNow = (row, col) =>
    can('billing', 'edit') && row.state !== 'cancelled' &&
    (row.state === 'draft' || ALWAYS_EDITABLE.includes(col));

  const lockReason = row =>
    row.state === 'cancelled'
      ? 'This invoice is cancelled — nothing can be edited.'
      : 'Locked once issued. Use Cancel & revise to change it.';

  const saveCell = async (row, col, val) => {
    const clean = typeof val === 'string' ? val.trim() : val;
    try {
      await api.put(`/billing/${row.id}`, { [col]: clean === '' ? null : clean });
      setInvoices(prev => patchRows(prev, row.id, { [col]: clean === '' ? null : clean }));
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not save');
      throw e;
    }
  };

  // Cell renderer: editable cells use the shared EditableCell; locked ones show
  // a lock on hover with the reason and a shortcut to Cancel & revise
  const inlineCell = (row, col, display, editOptions) => {
    if (editableNow(row, col)) {
      return (
        <EditableCellInline
          row={row} col={col} display={display} editOptions={editOptions}
          onSave={val => saveCell(row, col, val)}
        />
      );
    }
    return (
      <span className="group/lock inline-flex items-center gap-1" title={lockReason(row)}>
        {display}
        {row.state === 'issued' && can('billing', 'delete') && (
          <button onClick={e => { e.stopPropagation(); reviseInvoice(row); }}
            className="opacity-0 group-hover/lock:opacity-100 text-gray-300 hover:text-blue-500 transition-opacity"
            title="Locked once issued — click to cancel & revise">
            <Lock size={11} />
          </button>
        )}
      </span>
    );
  };

  const columns = [
    { key: 'invoice_number', label: 'Invoice #', render: (v, row) => (
      <span className={`font-mono text-sm font-semibold ${cancelled(row) ? 'text-gray-400 line-through' : 'text-[#1e3a5f]'}`}>{v}</span>
    )},
    { key: 'state', label: 'State', render: (v, row) => {
      const s = INV_STATES.find(x => x.v === (v || 'draft')) || INV_STATES[0];
      return (
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`} title={row.cancel_reason || ''}>
          {s.label}{row.revised_from_id ? ' (revision)' : ''}
        </span>
      );
    }},
    { key: 'client_name', label: 'Client', render: (v, row) => <span className={strike(row)}>{v}</span> },
    { key: 'invoice_date', label: 'Date', render: (v, row) =>
      inlineCell(row, 'invoice_date', <span className={strike(row)}>{fmtDate(v)}</span>, { type: 'date' }) },
    { key: 'due_date', label: 'Due', render: (v, row) =>
      inlineCell(row, 'due_date',
        <span className={strike(row)}>{v ? fmtDate(v) : <span className="text-gray-300">—</span>}</span>, { type: 'date' }) },
    { key: 'total', label: 'Total', render: (v, row) => <span className={`font-semibold ${strike(row)}`}>{fmtCurrency(v)}</span> },
    { key: 'paid_amount', label: 'Received', render: (v, row) => <span className={strike(row)}>{fmtCurrency(v)}</span> },
    { key: 'outstanding', label: 'Balance', render: (v, row) =>
      cancelled(row) ? <span className="text-gray-400">—</span>
        : row.state === 'draft' ? <span className="text-gray-400">not issued</span>
        : v > 0 ? <span className="text-red-600 font-semibold">{fmtCurrency(v)}</span> : <span className="text-green-600">Nil</span> },
    { key: 'derived_status', label: 'Payment', render: (v, row) => (
      cancelled(row) || row.state === 'draft' ? <span className="text-xs text-gray-400">—</span>
        : <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[v] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABEL[v] || v}</span>
    )},
    { key: 'notes', label: 'Notes', sortable: false, render: (v, row) =>
      inlineCell(row, 'notes',
        <span className={`text-xs ${strike(row)}`} style={{ color: v ? 'inherit' : '#d1d5db' }}>
          {v ? (String(v).length > 28 ? String(v).slice(0, 28) + '…' : v) : 'add note'}
        </span>, { type: 'text' }) },
    { key: 'id', label: '', sortable: false, render: (v, row) => (
      <div className="flex items-center gap-2">
        {row.state === 'draft' && can('billing', 'edit') && (
          <button onClick={() => issueInvoice(row)} title="Assign the real invoice number and lock the document"
            className="text-xs bg-[#1e3a5f] text-white px-2.5 py-1 rounded-lg flex items-center gap-1 hover:opacity-90">
            <Send size={12} /> Issue
          </button>
        )}
        {row.state === 'draft' && can('billing', 'delete') && (
          <button onClick={() => setDeleteTarget(row)} title="Delete this draft" className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
        )}
        {row.state === 'issued' && row.outstanding > 0 && can('billing', 'edit') && (
          <button onClick={() => setPayInvoice(row)}
            className="text-xs bg-[#1e3a5f] text-white px-2.5 py-1 rounded-lg flex items-center gap-1 hover:opacity-90">
            <Receipt size={12} /> Record payment
          </button>
        )}
        {row.state === 'issued' && can('billing', 'delete') && (
          <>
            <button onClick={() => { setCancelTarget(row); setCancelReason(''); }} title="Cancel this invoice (keeps the number)" className="text-gray-300 hover:text-red-500"><Ban size={14} /></button>
            <button onClick={() => reviseInvoice(row)} title="Cancel and open an editable copy" className="text-gray-300 hover:text-blue-500"><RotateCcw size={14} /></button>
          </>
        )}
        <button onClick={() => openInvoiceView(row)} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]" title="View invoice & payment history"><Printer size={15} /></button>
      </div>
    )},
  ];

  const totals = calcTotals(form.services.filter(s => s.name && s.amount));

  return (
    <div>
      <PageHeader title="Billing & Fees" subtitle="Invoices and outstanding fees"
        actions={
          <div className="flex gap-2">
            {can('billing', 'edit') && (
              <>
                <button onClick={() => setPayExport(true)} className="btn-secondary flex items-center gap-2 text-sm" title="Export outstanding invoices to fill in Excel">
                  <Download size={15} /> Payment sheet
                </button>
                <button onClick={() => setPayImport(true)} className="btn-secondary flex items-center gap-2 text-sm" title="Import the filled payment sheet">
                  <Upload size={15} /> Import payments
                </button>
              </>
            )}
            <button onClick={() => downloadExport('/export/invoices', { status: statusFilter }, `KUA_Invoices_${new Date().toISOString().split('T')[0]}.xlsx`)} className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>
            <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> New Invoice</button>
          </div>
        } />

      <div className="flex gap-1 mb-4 flex-wrap">
        {[['invoices', 'All Invoices'], ['receipts', 'Receipts'], ['outstanding', 'Outstanding Aging'], ['ledger', 'Client Ledger'], ['unbilled', 'Unbilled Work'], ['referring', 'Referring CAs']].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === t ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{l}</button>
        ))}
      </div>

      {tab === 'invoices' && (
        <div className="card">
          <div className="flex flex-wrap gap-3 mb-4">
            <SearchBar value={search} onChange={setSearch} placeholder="Search client or invoice..." />
            <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} className="input-field w-40">
              <option value="">All states</option>
              {INV_STATES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
          {selected.size > 0 && (
            <BulkActionBar
              module="billing"
              count={selected.size}
              onClear={() => setSelected(new Set())}
              extra={({ ask }) => {
                const s = selectionSummary();
                return (
                  <>
                    {s.drafts > 0 && (
                      <button onClick={() => ask(`Issue ${s.drafts} draft${s.drafts !== 1 ? 's' : ''}? Each gets its permanent invoice number now.`, bulkIssue)}
                        className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg bg-[#1e3a5f] text-white">
                        <Send size={12} /> Issue {s.drafts}
                      </button>
                    )}
                    <span className="flex items-center gap-1">
                      <input type="date" value={bulkDue} onChange={e => setBulkDue(e.target.value)} className="input-field text-xs py-1 w-36" title="Set due date" />
                      <button disabled={!bulkDue}
                        onClick={() => ask(`Set the due date to ${fmtDate(bulkDue)} on ${selected.size} invoice${selected.size !== 1 ? 's' : ''}?`, bulkSetDue)}
                        className="text-xs px-2 py-1.5 rounded-lg bg-[#1e3a5f] text-white disabled:opacity-40">Set due</button>
                    </span>
                    <button onClick={() => setBulkNote(true)} className="text-xs px-2 py-1.5 rounded-lg border border-gray-300 text-gray-600">Add note</button>
                    {s.withBalance.length > 0 && (
                      <button onClick={() => setBulkPay(true)} className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border border-green-300 text-green-700">
                        <Receipt size={12} /> Record payment ({s.withBalance.length})
                      </button>
                    )}
                    {s.issued > 0 && can('billing', 'delete') && (
                      <button onClick={() => { setBulkCancelReason(''); setBulkCancel(true); }}
                        className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg bg-red-600 text-white">
                        <Ban size={12} /> Cancel {s.issued}
                      </button>
                    )}
                    <button onClick={exportSelected} className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border border-gray-300 text-gray-600">
                      <Download size={12} /> Export
                    </button>
                  </>
                );
              }}
            />
          )}
          <DataTable
            columns={columns}
            data={filteredInvoices}
            selected={selected}
            onToggle={id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })}
            onToggleAll={rows => setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)))}
            onSelectRange={ids => setSelected(prev => { const n = new Set(prev); ids.forEach(i => n.add(i)); return n; })}
          />
        </div>
      )}

      {tab === 'receipts' && <ReceiptsTab />}

      {tab === 'outstanding' && (
        <div className="space-y-4">
          {['0-30', '31-60', '61-90', '90+'].map(bucket => {
            const items = outstanding.filter(i => i.aging_bucket === bucket);
            const total = items.reduce((s, i) => s + (i.outstanding || 0), 0);
            const colors = { '0-30': 'border-green-400', '31-60': 'border-yellow-400', '61-90': 'border-orange-400', '90+': 'border-red-500' };
            if (!items.length) return null;
            return (
              <div key={bucket} className={`card border-l-4 ${colors[bucket]}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-700">{bucket} Days — {items.length} invoices</h3>
                  <span className="text-lg font-bold text-gray-800">{fmtCurrency(total)}</span>
                </div>
                <div className="space-y-2">
                  {items.map(i => (
                    <div key={i.id} className="flex justify-between text-sm text-gray-600">
                      <span>{i.client_name} — {i.invoice_number}</span>
                      <span className="font-semibold text-red-600">{fmtCurrency(i.outstanding)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'ledger' && (
        <div className="space-y-4">
          <div className="card">
            <div className="flex flex-wrap gap-3 items-end mb-4">
              <div className="flex-1 min-w-48">
                <label className="block text-xs font-medium text-gray-500 mb-1">Select Client</label>
                <ClientSearchDropdown value={ledgerClientId} initialLabel={ledgerClientName} onChange={(id, name) => { setLedgerClientId(id); setLedgerClientName(name); loadLedger(id); }} />
              </div>
              {ledgerClientId && (
                <>
                  <button onClick={() => downloadExport('/ledger/ledger/export', { client_id: ledgerClientId }, `KUA_Ledger_${ledgerClientName.replace(/\s/g,'_')}_${new Date().toISOString().split('T')[0]}.xlsx`)} className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>
                  <button onClick={() => { setReceiptForm({ amount: '', payment_mode: 'Bank Transfer', receipt_date: new Date().toISOString().split('T')[0] }); setShowReceiptModal(true); }} className="btn-primary flex items-center gap-2 text-sm"><Receipt size={15} /> Record Payment</button>
                </>
              )}
            </div>
            {ledgerLoaded && ledgerData.length === 0 && <p className="text-gray-400 text-sm">No transactions found for this client.</p>}
            {ledgerData.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Particulars</th>
                    <th className="px-3 py-2 text-right">Debit (₹)</th><th className="px-3 py-2 text-right">Credit (₹)</th>
                    <th className="px-3 py-2 text-right">Balance (₹)</th><th className="px-3 py-2">Dr/Cr</th>
                  </tr></thead>
                  <tbody>
                    {ledgerData.map((e, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(e.date)}</td>
                        <td className="px-3 py-2">{e.particulars}</td>
                        <td className="px-3 py-2 text-right">{e.debit ? fmtCurrency(e.debit) : '—'}</td>
                        <td className="px-3 py-2 text-right text-green-700">{e.credit ? fmtCurrency(e.credit) : '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmtCurrency(Math.abs(e.balance))}</td>
                        <td className="px-3 py-2 text-center"><span className={`text-xs px-1.5 py-0.5 rounded font-medium ${e.balance >= 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{e.balance_type}</span></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-gray-100 font-bold text-sm">
                    <td colSpan={2} className="px-3 py-2">Closing Balance</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(ledgerData.reduce((s,e) => s + (e.debit||0), 0))}</td>
                    <td className="px-3 py-2 text-right text-green-700">{fmtCurrency(ledgerData.reduce((s,e) => s + (e.credit||0), 0))}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(Math.abs(ledgerData[ledgerData.length-1]?.balance || 0))}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-xs px-1.5 py-0.5 rounded font-medium ${(ledgerData[ledgerData.length-1]?.balance||0) >= 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{ledgerData[ledgerData.length-1]?.balance_type}</span></td>
                  </tr></tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'unbilled' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Completed work with no invoice raised — select items and generate the invoice per client.
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={includeGstU} onChange={e => setIncludeGstU(e.target.checked)} className="rounded" />
              Add GST @{gstRate}%
            </label>
          </div>
          {unbilled.length === 0 ? (
            <div className="card flex flex-col items-center py-12 text-gray-400"><FileWarning size={40} className="mb-2" /><p>No unbilled completed work found</p></div>
          ) : (
            Object.entries(unbilled.reduce((g, u) => {
              // Referring-CA clients group under the CA; direct clients under themselves
              const key = u.referring_ca_id ? `rca:${u.referring_ca_id}` : `c:${u.client_id}`;
              if (!g[key]) g[key] = u.referring_ca_id
                ? { name: u.referring_ca_name, isRca: true, rcaId: u.referring_ca_id, items: [] }
                : { name: u.client_name, isRca: false, clientId: u.client_id, items: [] };
              g[key].items.push(u);
              return g;
            }, {})).map(([gkey, g]) => {
              const checkedItems = g.items.filter(u => unbilledSelected.has(u.work_type + ':' + u.id));
              const subtotal = checkedItems.reduce((s, u) => s + (parseFloat(unbilledFees[u.work_type + ':' + u.id] ?? u.default_fee ?? 0) || 0), 0);
              const total = includeGstU ? subtotal * (1 + gstRate / 100) : subtotal;
              return (
                <div key={gkey} className={`card ${g.isRca ? 'border-amber-200 bg-amber-50/30' : ''}`}>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h3 className="font-semibold text-gray-800">{g.name}</h3>
                    {g.isRca && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Referring CA — billed to CA, not end-clients</span>}
                    <span className="text-xs text-gray-400">{g.items.length} unbilled item{g.items.length !== 1 ? 's' : ''}{g.isRca ? ` across ${new Set(g.items.map(i => i.client_id)).size} client(s)` : ''}</span>
                    {checkedItems.length > 0 && can('billing', 'create') && (
                      <div className="ml-auto flex items-center gap-3">
                        <span className="text-sm text-gray-600">Total: <strong>{fmtCurrency(total)}</strong>{includeGstU && <span className="text-xs text-gray-400"> (incl. GST)</span>}</span>
                        <button onClick={() => generateForGroup(g, checkedItems)} disabled={generating}
                          className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
                          <Plus size={14} /> {generating ? 'Generating…' : `Generate Invoice (${checkedItems.length})`}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                        <th className="px-3 py-2 w-8"><input type="checkbox" className="rounded"
                          checked={g.items.every(u => unbilledSelected.has(u.work_type + ':' + u.id))}
                          onChange={e => { const s = new Set(unbilledSelected); g.items.forEach(u => { const k = u.work_type + ':' + u.id; e.target.checked ? s.add(k) : s.delete(k); }); setUnbilledSelected(s); }} /></th>
                        <th className="px-3 py-2 text-left">Work Type</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">Period</th>
                        <th className="px-3 py-2 text-left">Completed</th>
                        <th className="px-3 py-2 text-left">Staff</th>
                        <th className="px-3 py-2 text-right w-32">Fee (₹)</th>
                      </tr></thead>
                      <tbody>
                        {g.items.map(u => {
                          const key = u.work_type + ':' + u.id;
                          return (
                            <tr key={key} className={`border-b ${unbilledSelected.has(key) ? 'bg-blue-50/40' : 'hover:bg-gray-50'}`}>
                              <td className="px-3 py-2 text-center"><input type="checkbox" className="rounded" checked={unbilledSelected.has(key)} onChange={e => { const s = new Set(unbilledSelected); e.target.checked ? s.add(key) : s.delete(key); setUnbilledSelected(s); }} /></td>
                              <td className="px-3 py-2"><span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{u.work_type}</span></td>
                              <td className="px-3 py-2">{u.work_description}{g.isRca && <span className="text-xs text-amber-700 ml-1.5">for {u.client_name}</span>}</td>
                              <td className="px-3 py-2">{u.period}</td>
                              <td className="px-3 py-2 text-gray-500">{fmtDate(u.completed_date)}</td>
                              <td className="px-3 py-2 text-gray-500">{u.staff_name || '—'}</td>
                              <td className="px-3 py-2 text-right">
                                <input type="number" className="input-field text-right text-xs py-1 w-28"
                                  value={unbilledFees[key] ?? u.default_fee ?? 0}
                                  onChange={e => setUnbilledFees(f => ({ ...f, [key]: e.target.value }))} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === 'referring' && <ReferringCAsTab can={can} />}

      {showReceiptModal && (
        <Modal title="Record Payment" onClose={() => setShowReceiptModal(false)}>
          <form onSubmit={saveReceipt} className="space-y-3">
            <FormField label="Amount (₹)" required><input required type="number" step="0.01" value={receiptForm.amount} onChange={e => setReceiptForm(f => ({...f, amount: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Payment Date"><input type="date" value={receiptForm.receipt_date} onChange={e => setReceiptForm(f => ({...f, receipt_date: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Payment Mode">
              <select value={receiptForm.payment_mode} onChange={e => setReceiptForm(f => ({...f, payment_mode: e.target.value}))} className="input-field">
                {['Cash', 'Bank Transfer', 'Cheque', 'UPI', 'NEFT', 'RTGS'].map(m => <option key={m}>{m}</option>)}
              </select>
            </FormField>
            <FormField label="Reference No."><input value={receiptForm.reference_number||''} onChange={e => setReceiptForm(f => ({...f, reference_number: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Notes"><textarea value={receiptForm.notes||''} onChange={e => setReceiptForm(f => ({...f, notes: e.target.value}))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3"><button type="submit" className="btn-primary flex-1">Save Payment</button><button type="button" onClick={() => setShowReceiptModal(false)} className="btn-secondary flex-1">Cancel</button></div>
          </form>
        </Modal>
      )}

      {showModal && (
        <Modal title="New Invoice" onClose={() => setShowModal(false)} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Client" required>
                <ClientSearchDropdown
                  value={form.client_id || ''}
                  initialLabel={form.client_name || ''}
                  onChange={(id, name) => setForm(f => ({ ...f, client_id: id, client_name: name }))}
                  required
                />
              </FormField>
              <FormField label="Invoice Date"><input type="date" value={form.invoice_date} onChange={e => setForm(f => ({...f, invoice_date: e.target.value}))} className="input-field" /></FormField>
              <FormField label="Due Date"><input type="date" value={form.due_date||''} onChange={e => setForm(f => ({...f, due_date: e.target.value}))} className="input-field" /></FormField>
              <FormField label="Status"><select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="input-field">{INV_STATUSES.map(s => <option key={s}>{s}</option>)}</select></FormField>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Services</label>
              {form.services.map((svc, i) => (
                <div key={svc._k ?? i} className="flex gap-2 mb-2">
                  <input value={svc.name} onChange={e => { const s = [...form.services]; s[i].name = e.target.value; setForm(f => ({...f, services: s})); }} className="input-field flex-1" placeholder="Service description" />
                  <input type="number" value={svc.amount} onChange={e => { const s = [...form.services]; s[i].amount = e.target.value; setForm(f => ({...f, services: s})); }} className="input-field w-32" placeholder="Amount (₹)" />
                  {form.services.length > 1 && <button type="button" onClick={() => setForm(f => ({...f, services: f.services.filter((_, j) => j !== i)}))} className="text-red-400 hover:text-red-600">×</button>}
                </div>
              ))}
              <button type="button" onClick={() => setForm(f => ({...f, services: [...f.services, { name: '', amount: '', _k: Math.random() }]}))} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">+ Add Line</button>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmtCurrency(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">GST @{gstRate}%</span><span>{fmtCurrency(totals.gst_amount)}</span></div>
              <div className="flex justify-between font-bold text-gray-800 text-base border-t pt-1"><span>Total</span><span>{fmtCurrency(totals.total)}</span></div>
            </div>

            <FormField label="Notes"><textarea value={form.notes||''} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3"><button type="submit" className="btn-primary flex-1">Create Invoice</button><button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</button></div>
          </form>
        </Modal>
      )}

      {showView && (
        <Modal title="Invoice Preview" onClose={() => setShowView(null)} size="lg">
          <div className="bg-white p-6 font-sans text-sm" id="invoice-print">
            <div className="flex justify-between mb-8">
              <div>
                <div className="text-2xl font-bold text-[#1e3a5f]">Krishna Uday & Associates</div>
                <div className="text-gray-500 text-xs">Chartered Accountants, Hyderabad</div>
                <div className="text-gray-500 text-xs">GSTIN: 36ABCDE1234F1Z5</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-gray-700">INVOICE</div>
                <div className="font-mono font-bold text-[#f0a500]">{showView.invoice_number}</div>
                <div className="text-gray-500">Date: {fmtDate(showView.invoice_date)}</div>
              </div>
            </div>
            <div className="border rounded-lg p-4 mb-6 bg-gray-50">
              <div className="text-xs text-gray-500 mb-1">Bill To:</div>
              <div className="font-bold text-gray-700">{showView.client_name}</div>
            </div>
            <table className="w-full mb-6">
              <thead><tr className="border-b-2"><th className="text-left py-2 text-gray-600">Description</th><th className="text-right py-2 text-gray-600">Amount</th></tr></thead>
              <tbody>
                {showView.services?.map((s, i) => <tr key={i} className="border-b"><td className="py-2">{s.name}</td><td className="text-right py-2">{fmtCurrency(s.amount)}</td></tr>)}
              </tbody>
            </table>
            <div className="ml-auto w-64 space-y-1">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{fmtCurrency(showView.subtotal)}</span></div>
              <div className="flex justify-between text-gray-500"><span>GST @{showView.subtotal > 0 ? Math.round((showView.gst_amount / showView.subtotal) * 100) : gstRate}%</span><span>{fmtCurrency(showView.gst_amount)}</span></div>
              <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span className="text-[#1e3a5f]">{fmtCurrency(showView.total)}</span></div>
            </div>
            <div className="mt-8 text-center text-xs text-gray-400">Thank you for your business</div>

            {/* Payment history — every individual payment, not one flag */}
            <div className="mt-6 border-t pt-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Payment History</h4>
              {viewPayments === null ? (
                <p className="text-xs text-gray-400">Loading payments…</p>
              ) : viewPayments.payments.length === 0 ? (
                <p className="text-xs text-gray-400">No payments recorded yet.</p>
              ) : (
                <>
                  <table className="w-full text-xs">
                    <thead><tr className="text-gray-400 uppercase border-b">
                      <th className="text-left py-1.5">Receipt #</th><th className="text-left py-1.5">Date</th>
                      <th className="text-right py-1.5">Amount</th><th className="text-left py-1.5 pl-4">Mode</th>
                      <th className="text-left py-1.5">Bank/UPI</th><th className="text-left py-1.5">Reference</th>
                    </tr></thead>
                    <tbody>
                      {viewPayments.payments.map(pmt => (
                        <tr key={pmt.id} className="border-b border-gray-50">
                          <td className="py-1.5 font-mono text-gray-600">{pmt.receipt_number}</td>
                          <td className="py-1.5">{fmtDate(pmt.receipt_date)}</td>
                          <td className="py-1.5 text-right font-semibold text-green-700">{fmtCurrency(pmt.amount)}</td>
                          <td className="py-1.5 pl-4">{pmt.payment_mode}</td>
                          <td className="py-1.5 text-gray-500">{pmt.bank_or_upi || '—'}</td>
                          <td className="py-1.5 text-gray-500">{pmt.reference_number || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-end gap-6 mt-2 text-xs">
                    <span>Paid: <strong className="text-green-700">{fmtCurrency(viewPayments.paid)}</strong></span>
                    <span>Outstanding: <strong className={viewPayments.outstanding > 0 ? 'text-red-600' : 'text-green-700'}>{fmtCurrency(viewPayments.outstanding)}</strong></span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            {viewPayments?.outstanding > 0 && can('billing', 'edit') && (
              <button onClick={() => { setPayInvoice({ ...showView, outstanding: viewPayments.outstanding }); }} className="btn-primary flex-1 flex items-center justify-center gap-2"><Receipt size={16} /> Record Payment</button>
            )}
            <button onClick={() => window.print()} className="btn-secondary flex-1 flex items-center justify-center gap-2"><Printer size={16} /> Print</button>
            <button onClick={() => setShowView(null)} className="btn-secondary flex-1">Close</button>
          </div>
        </Modal>
      )}

      {bulkNote && (
        <Modal title={`Add a note to ${selected.size} invoice${selected.size !== 1 ? 's' : ''}`} onClose={() => setBulkNote(false)} size="md">
          <div className="space-y-3">
            <p className="text-xs text-gray-500">The note is added to each invoice's existing notes — nothing is overwritten.</p>
            <input autoFocus value={bulkNoteText} onChange={e => setBulkNoteText(e.target.value)} className="input-field w-full" placeholder="e.g. Reminder sent 3 Aug" />
            <div className="flex gap-3">
              <button onClick={bulkAddNote} disabled={!bulkNoteText.trim()} className="btn-primary flex-1 disabled:opacity-40">Add note</button>
              <button onClick={() => setBulkNote(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {bulkCancel && (() => { const s = selectionSummary(); return (
        <Modal title="Cancel invoices" onClose={() => setBulkCancel(false)} size="md">
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-700">
                <p className="font-semibold">Cancel {s.issued} issued invoice{s.issued !== 1 ? 's' : ''} totalling {fmtCurrency(s.issuedTotal)}?</p>
                <p className="text-xs mt-1">
                  They stay in the list struck through and keep their numbers forever. Their billed work returns to Unbilled Work.
                  {selected.size > s.issued && ` ${selected.size - s.issued} selected row(s) are drafts or already cancelled and will be left alone.`}
                </p>
              </div>
            </div>
            <FormField label="Reason (applies to all)" required>
              <input autoFocus value={bulkCancelReason} onChange={e => setBulkCancelReason(e.target.value)} className="input-field w-full" placeholder="e.g. Duplicate batch" />
            </FormField>
            <div className="flex gap-3">
              <button onClick={doBulkCancel} disabled={!bulkCancelReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2 rounded-lg font-medium text-sm">
                Cancel {s.issued} invoice{s.issued !== 1 ? 's' : ''}
              </button>
              <button onClick={() => setBulkCancel(false)} className="btn-secondary flex-1">Keep them</button>
            </div>
          </div>
        </Modal>
      ); })()}

      {bulkPay && (() => { const s = selectionSummary();
        const total = s.withBalance.reduce((x, r) => x + r.outstanding, 0);
        return (
        <Modal title="Record payment on selected invoices" onClose={() => setBulkPay(false)} size="md">
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              One receipt per invoice for its <strong>full remaining balance</strong> — {s.withBalance.length} invoice{s.withBalance.length !== 1 ? 's' : ''}, {fmtCurrency(total)} in total.
            </p>
            <div className="max-h-32 overflow-y-auto text-xs border border-gray-100 rounded-lg divide-y divide-gray-50">
              {s.withBalance.map(r => (
                <div key={r.id} className="flex justify-between px-3 py-1.5">
                  <span className="font-mono">{r.invoice_number}</span>
                  <span className="text-gray-500">{r.client_name}</span>
                  <span className="font-semibold">{fmtCurrency(r.outstanding)}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Date received"><input type="date" value={bulkPayForm.payment_date} onChange={e => setBulkPayForm(f => ({ ...f, payment_date: e.target.value }))} className="input-field" /></FormField>
              <FormField label="Mode">
                <select value={bulkPayForm.payment_mode} onChange={e => setBulkPayForm(f => ({ ...f, payment_mode: e.target.value }))} className="input-field">
                  {['Cash', 'Cheque', 'NEFT', 'UPI', 'Other'].map(m => <option key={m}>{m}</option>)}
                </select>
              </FormField>
              <FormField label="Reference"><input value={bulkPayForm.reference_number} onChange={e => setBulkPayForm(f => ({ ...f, reference_number: e.target.value }))} className="input-field" placeholder="UTR / cheque" /></FormField>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={doBulkPayment} className="btn-primary flex-1">Record {fmtCurrency(total)}</button>
              <button onClick={() => setBulkPay(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      ); })()}

      {payExport && <PaymentExportModal onClose={() => setPayExport(false)} />}

      {payImport && (
        <PaymentImportModal
          onClose={() => setPayImport(false)}
          onImported={data => {
            // Grid updates from the commit response — no refetch
            if (data.invoices) setInvoices(data.invoices);
            api.get('/billing/reports/outstanding').then(r => setOutstanding(r.data)).catch(() => {});
          }}
        />
      )}

      {cancelTarget && (
        <Modal title="Cancel invoice" onClose={() => setCancelTarget(null)} size="md">
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-700">
                <p className="font-semibold">Cancel {cancelTarget.invoice_number} — {fmtCurrency(cancelTarget.total)}?</p>
                <p className="text-xs mt-1">{cancelTarget.client_name}, dated {fmtDate(cancelTarget.invoice_date)}.</p>
                <p className="text-xs mt-2">
                  The invoice stays in the list (struck through) and keeps its number forever — the number is never reused.
                  It stops counting towards revenue and outstanding, and its billed work returns to Unbilled Work.
                  {cancelTarget.paid_amount > 0 && <> <strong>Note: {fmtCurrency(cancelTarget.paid_amount)} has already been received against it — those receipts stay in the ledger.</strong></>}
                </p>
              </div>
            </div>
            <FormField label="Reason for cancelling" required>
              <input autoFocus value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                className="input-field w-full" placeholder="e.g. Wrong client / duplicate / amount incorrect" />
            </FormField>
            <div className="flex gap-3">
              <button onClick={confirmCancel} disabled={!cancelReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2 rounded-lg font-medium text-sm">
                Cancel this invoice
              </button>
              <button onClick={() => setCancelTarget(null)} className="btn-secondary flex-1">Keep it</button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title="Delete draft" onClose={() => setDeleteTarget(null)} size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Delete draft <strong>{deleteTarget.invoice_number}</strong> ({fmtCurrency(deleteTarget.total)})?
              Its work items return to Unbilled Work. Drafts have no invoice number in the sequence, so nothing is lost.
            </p>
            <div className="flex gap-3">
              <button onClick={() => deleteDraft(deleteTarget)} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium text-sm">Delete draft</button>
              <button onClick={() => setDeleteTarget(null)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {payInvoice && (
        <RecordPaymentModal
          invoice={payInvoice}
          onClose={() => setPayInvoice(null)}
          onSaved={() => {
            setPayInvoice(null);
            load();
            api.get('/billing/reports/outstanding').then(r => setOutstanding(r.data)).catch(() => {});
            if (showView) openInvoiceView(showView);
          }}
        />
      )}
    </div>
  );
}
